import { useEffect, useMemo, useState } from 'react';
import { Form, Formik } from 'formik';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { ArrowLeft, Check } from 'lucide-react';
import * as Yup from 'yup';

import { Button } from 'components/ui/Button';
import { GenericLoading } from 'components/ui/Loader';
import { FormikDropdown } from 'components/forms/formik/FormikDropdown';

import { CodeEntrepriseField } from '../components/CodeEntrepriseField';
import {
  createPlateforme,
  getCabinetsEligiblesAdministration,
  getPmEligiblesGouvernance,
  PmEligibleDto,
} from '../api/plateformesApi';

interface TitulaireFormValues {
  code: string;
  personneMoraleId: string;
}

const validationSchema = Yup.object({
  personneMoraleId: Yup.string().required('La personne morale titulaire est requise'),
});

interface PlateformeTitulaireExistantFormProps {
  estAdministree: boolean;
  onBack: () => void;
}

/**
 * Formulaire léger des chemins « PM existante » et « plateforme administrée » :
 * la PM titulaire existe déjà, il n'y a rien à provisionner — sélection de la
 * PM (annuaire filtré côté serveur : natures éligibles, jamais déjà titulaire)
 * et code entreprise. La plateforme prend la DÉSIGNATION de la PM comme nom ;
 * le thème et les logos se configurent ensuite sur la page détail.
 */
export const PlateformeTitulaireExistantForm: React.FC<PlateformeTitulaireExistantFormProps> = ({
  estAdministree,
  onBack,
}) => {
  const navigate = useNavigate();
  const [candidates, setCandidates] = useState<PmEligibleDto[]>([]);
  const [isLoadingCandidates, setIsLoadingCandidates] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setIsLoadingCandidates(true);
    (estAdministree ? getCabinetsEligiblesAdministration() : getPmEligiblesGouvernance())
      .then((items) => {
        if (!cancelled) setCandidates(items);
      })
      .catch(() => {
        if (!cancelled) {
          toast.error('Erreur de chargement des personnes morales éligibles.');
        }
      })
      .finally(() => {
        if (!cancelled) setIsLoadingCandidates(false);
      });
    return () => {
      cancelled = true;
    };
  }, [estAdministree]);

  const items = useMemo(
    () =>
      candidates.map((pm) => ({
        key: pm.id,
        label: pm.sousNatureLibelle
          ? `${pm.designation} — ${pm.sousNatureLibelle}`
          : pm.natureLibelle
            ? `${pm.designation} — ${pm.natureLibelle}`
            : (pm.designation ?? ''),
      })),
    [candidates],
  );

  const onSubmit = async (values: TitulaireFormValues) => {
    // Le nom de la plateforme est la désignation de la PM titulaire.
    const pm = candidates.find((candidate) => candidate.id === values.personneMoraleId);
    if (!pm?.designation) {
      toast.error('La personne morale sélectionnée n\'a pas de désignation.');
      return;
    }

    setIsSubmitting(true);
    try {
      const response = await createPlateforme({
        name: pm.designation,
        code: values.code || null,
        modeGestion: estAdministree ? 'ADMINISTREE' : 'GEREE',
        mainPersonneMoraleId: values.personneMoraleId,
      });

      if (!response.isSuccess || !response.id) {
        toast.error(response.errorMessage || 'La création de la plateforme a échoué.');
        return;
      }

      toast.success(
        estAdministree
          ? 'Plateforme administrée créée — habilitez maintenant ses produits.'
          : 'Plateforme créée — sa personne morale gérante a reçu la gouvernance.',
      );
      navigate(`/back-office/plateformes/${response.id}`);
    } catch (error) {
      console.error('Error on submit plateforme (titulaire existant):', error);
      toast.error('La création de la plateforme a échoué.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Formik<TitulaireFormValues>
      initialValues={{ code: '', personneMoraleId: '' }}
      validationSchema={validationSchema}
      onSubmit={onSubmit}
    >
      <Form className="relative flex flex-col gap-6">
        {isSubmitting && <GenericLoading loading isParentSized />}

        <TitulaireFields
          items={items}
          isLoadingCandidates={isLoadingCandidates}
          estAdministree={estAdministree}
        />

        <div className="flex items-center justify-between">
          <Button outline type="button" onClick={onBack}>
            <ArrowLeft data-slot="icon" />
            Changer de mode de création
          </Button>
          <Button color="primary" type="submit" disabled={isSubmitting}>
            <Check data-slot="icon" />
            Créer la plateforme
          </Button>
        </div>
      </Form>
    </Formik>
  );
};

const TitulaireFields: React.FC<{
  items: { key: string; label: string }[];
  isLoadingCandidates: boolean;
  estAdministree: boolean;
}> = ({ items, isLoadingCandidates, estAdministree }) => {
  return (
    <div className="rounded-xl bg-section p-6 flex flex-col gap-4">
      <div>
        <FormikDropdown
          name="personneMoraleId"
          label={estAdministree ? 'Personne morale titulaire' : 'Personne morale gérante'}
          items={items}
          placeholder={
            isLoadingCandidates
              ? 'Chargement…'
              : items.length
                ? 'Sélectionner une personne morale'
                : 'Aucune personne morale éligible (toutes sont déjà titulaires)'
          }
        />
        <p className="mt-1 text-xs text-gray-500">
          {estAdministree
            ? 'Cabinets de courtage et compagnies sans plateforme sont proposés. Le titulaire ne gouverne pas : vous piloterez ses produits depuis les habilitations. '
            : 'Seules les compagnies et courtiers grossistes sans plateforme sont proposés. '}
          La plateforme prendra la désignation de la personne morale comme nom.
        </p>
      </div>

      <div className="grid grid-cols-2 gap-4">
        <CodeEntrepriseField name="code" autoGenerateIfEmpty />
      </div>
    </div>
  );
};
